import type {Anchor} from './use-rows.ts';

/**
 * Associates an anchor with list query parameters.
 *
 * @typeParam TListContextParams - The type of parameters that define the list's query context
 * @typeParam TStartRow - The type of data needed to anchor pagination
 */
export type QueryAnchor<TListContextParams, TStartRow> = {
  readonly anchor: Anchor<TStartRow>;
  /**
   * Associates an anchor with list query params to coordinate state during
   * navigation. When list context params change (e.g., filter/sort changes or
   * browser back/forward navigation), the anchor and scroll position must be
   * updated atomically with the new query results.
   *
   * When `listContextParams !== queryAnchor.listContextParams`:
   * - Use history state to restore previous scroll position and anchor if
   *   navigating back
   * - Use permalink anchor if loading a specific item
   * - Otherwise reset to top
   *
   * During the transition (while `!isListContextCurrent`), skip paging logic
   * and count updates to avoid querying with mismatched anchor/params or
   * calculating counts from inconsistent state.
   */
  readonly listContextParams: TListContextParams;
};

/**
 * State object that captures the virtualizer's pagination state.
 *
 * @typeParam TListContextParams - The type of parameters that define the list's query context
 * @typeParam TStartRow - The type of data needed to anchor pagination
 */
export type PagingState<TListContextParams, TStartRow> = {
  estimatedTotal: number;
  hasReachedStart: boolean;
  hasReachedEnd: boolean;
  queryAnchor: QueryAnchor<TListContextParams, TStartRow>;
  pagingPhase: 'idle' | 'adjusting' | 'skipping';
  pendingScrollAdjustment: number;
};

/**
 * Reducer function for managing the pagination state of the virtualizer.
 *
 * @typeParam TListContextParams - The type of parameters that define the list's query context
 * @typeParam TStartRow - The type of data needed to anchor pagination
 */
export function pagingReducer<TListContextParams, TStartRow>(
  state: PagingState<TListContextParams, TStartRow>,
  action: PagingAction<TListContextParams, TStartRow>,
): PagingState<TListContextParams, TStartRow> {
  switch (action.type) {
    case 'UPDATE_ESTIMATED_TOTAL': {
      const newTotal = Math.max(state.estimatedTotal, action.newTotal);
      if (newTotal === state.estimatedTotal) {
        return state;
      }
      return {
        ...state,
        estimatedTotal: newTotal,
      };
    }

    case 'REACHED_START':
      return {...state, hasReachedStart: true};

    case 'REACHED_END':
      return {...state, hasReachedEnd: true};

    case 'UPDATE_ANCHOR':
      return {
        ...state,
        queryAnchor: {
          ...state.queryAnchor,
          anchor: action.anchor,
        },
        pagingPhase: 'skipping',
      };

    case 'SHIFT_ANCHOR_DOWN':
      return {
        ...state,
        queryAnchor: {
          ...state.queryAnchor,
          anchor: action.newAnchor,
        },
        pendingScrollAdjustment: action.offset,
        pagingPhase: 'adjusting',
      };

    case 'RESET_TO_TOP':
      return {
        ...state,
        queryAnchor: {
          ...state.queryAnchor,
          anchor: {index: 0, kind: 'forward', startRow: undefined},
        },
        pendingScrollAdjustment: action.offset,
        pagingPhase: 'adjusting',
      };

    case 'SCROLL_ADJUSTED':
      return {
        ...state,
        estimatedTotal: state.estimatedTotal + state.pendingScrollAdjustment,
        pendingScrollAdjustment: 0,
        pagingPhase: 'skipping',
      };

    case 'PAGING_COMPLETE':
      return {
        ...state,
        pagingPhase: 'idle',
      };

    case 'RESET_STATE':
      return {
        ...state,
        estimatedTotal: action.estimatedTotal,
        hasReachedStart: action.hasReachedStart,
        hasReachedEnd: action.hasReachedEnd,
        queryAnchor: {
          listContextParams: action.listContextParams,
          anchor: action.anchor,
        },
        pagingPhase: 'skipping',
      };

    default: {
      action satisfies never;
      return state;
    }
  }
}

/**
 * Actions that can be dispatched to the paging reducer.
 *
 * @typeParam TListContextParams - The type of parameters that define the list's query context
 * @typeParam TStartRow - The type of data needed to anchor pagination
 */
export type PagingAction<TListContextParams, TStartRow> =
  | {type: 'UPDATE_ESTIMATED_TOTAL'; newTotal: number}
  | {type: 'REACHED_START'}
  | {type: 'REACHED_END'}
  | {type: 'UPDATE_ANCHOR'; anchor: Anchor<TStartRow>}
  | {
      type: 'SHIFT_ANCHOR_DOWN';
      offset: number;
      newAnchor: Anchor<TStartRow>;
    }
  | {type: 'RESET_TO_TOP'; offset: number}
  | {type: 'SCROLL_ADJUSTED'}
  | {type: 'PAGING_COMPLETE'}
  | {
      type: 'RESET_STATE';
      estimatedTotal: number;
      hasReachedStart: boolean;
      hasReachedEnd: boolean;
      anchor: Anchor<TStartRow>;
      listContextParams: TListContextParams;
    };
